import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { Prescription } from '../_models/prescription.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { environment } from 'src/environments/environment';

@Injectable()
export class PrescriptionService {
  private authToken;
  private company_id;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.company_id = res.companyID));
  }

  private static getPrescriptionsListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&is_active=${filter.isActive}`;

    if (sortField.localeCompare('id') === 0) fromString += `&sort=prescription.id,${sortOrder}`;
    if (sortField.localeCompare('drug') === 0) fromString += `&sort=drug_name,${sortOrder}`;
    if (sortField.localeCompare('rx') === 0)
      fromString += `&sort=prescription.pms_rx_number,${sortOrder}`;
    if (sortField.localeCompare('doctor') === 0)
      fromString += `&sort=entity.first_name,${sortOrder}&sort=entity.last_name,${sortOrder}`;
    if (sortField.localeCompare('prescriptionDate') === 0)
      fromString += `&sort=prescription.prescribe_date,${sortOrder}`;
    if (sortField.localeCompare('lastFilledDate') === 0)
      fromString += `&sort=prescription.last_filled_date,${sortOrder}`;
    if (sortField.localeCompare('daysSupply') === 0)
      fromString += `&sort=prescription.fill_days_supply,${sortOrder}`;
    if (sortField.localeCompare('refill') === 0)
      fromString += `&sort=prescription.original_refill,${sortOrder}`;
    if (sortField.localeCompare('hold') === 0)
      fromString += `&sort=prescription.is_hold,${sortOrder}`;
    if (sortField.localeCompare('isActive') === 0)
      fromString += `&sort=prescription.is_active,${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.drug) fromString += `&drug_has=${filter.drug}`;
      if (filter.rx) fromString += `&pms_rx_has=${filter.rx}`;
      if (filter.doctor) fromString += `&doctor_has=${filter.doctor}`;
      if (filter.prescriptionDate) fromString += `&prescribe_date=${filter.prescriptionDate}`;
      if (filter.lastFilledDate) fromString += `&last_filled_date=${filter.lastFilledDate}`;
      if (filter.daysSupply) fromString += `&days_supply_has=${filter.daysSupply}`;
      if (filter.refill) fromString += `&refill_has=${filter.refill}`;
      if (filter.hold) fromString += `&is_hold=${filter.hold}`;
    }

    return fromString;
  }

  findPrescriptions(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: PrescriptionService.getPrescriptionsListQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.company_id}/patients/${queryParams.filter.patientId}/prescriptions`,
      { params },
    );
  }

  getPrescriptionsAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const params = new HttpParams({
      fromString: PrescriptionService.getPrescriptionsListQueryString(queryParams),
    });

    return this.http.get(
      `${environment.CORE_BASE}/companies/${this.company_id}/patients/${queryParams.filter.patientId}/prescriptions/csv`,
      {
        params,
        responseType: 'text',
      },
    );
  }

  createPrescription(_prescription: Prescription, patientId: number) {
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/patients/${patientId}/prescriptions`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<Prescription>(url, _prescription, { headers });
  }

  updatePrescription(_prescription: Prescription, patientId: number): Observable<any> {
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/patients/${patientId}/prescriptions/${_prescription.id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put<Prescription>(url, _prescription, { headers });
  }

  deletePrescription(prescriptionId: number, patientId: number): Observable<any> {
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/patients/${patientId}/prescriptions/${prescriptionId}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers });
  }
}
